// ============ QUANT WATCHLIST — nopoly research, dashboard card ============
// nopoly.ai's quant desk page (six sections: pre-market checklist, tech & AI,
// IPO watch, gurus, power producers, nuclear) served through our own proxy at
// /api/nopoly?feed=watchlist.
//
// Collapsed the card shows the checklist plus each section's title and thesis;
// expanded it renders every section. Upstream copy is English and stays that
// way — only the chrome is Thai. Prices shown are OURS (live via /api/quotes),
// never nopoly's daily snapshot.
import React, { useEffect, useMemo, useState } from 'react';
import { SecHead } from './core';
import { fetchStockQuotes } from '../services/api';
import { useT } from '../lib/i18n';

interface WStock { symbol: string; name: string; about?: string; marketCap?: string; }
interface WChecklist { time?: string; title: string; }
interface WStory { title: string; tag?: string; status?: string; detail?: string; source?: string; }
interface WPerson { name: string; role?: string; action?: string; tickers?: string; filing?: string; }
interface WGuru { name: string; org?: string; style?: string; buying?: string; selling?: string; note?: string; }
interface WSection {
  id: string; navLabel?: string; title: string; thesis?: string;
  stocks?: WStock[]; checklist?: WChecklist[]; stories?: WStory[]; people?: WPerson[]; gurus?: WGuru[];
}
interface Watchlist { title?: string; asOf?: string; source?: string; sections?: WSection[]; }

// Thai for the tile grid. The upstream section ids are stable, so these are
// keyed by id and fall back to the English original for anything new — a
// section we have not translated shows up in English rather than blank.
const TH_LABEL: Record<string, string> = {
  'pre-market': 'ก่อนตลาดเปิด',
  'tech-ai': 'เทค & AI',
  'ipo-watch': 'หุ้น IPO',
  gurus: 'กูรูระดับโลก',
  'power-top5': 'ผู้ผลิตไฟฟ้า',
  nuclear: 'นิวเคลียร์',
};

const TH_THESIS: Record<string, string> = {
  'pre-market': 'สามอย่างที่ต้องเช็กก่อนตลาดสหรัฐเปิด (20:30 น. เวลาไทย) — สัญญาณสดและโอกาสของวันนี้อัปเดตให้อัตโนมัติ ส่วนราคาทั้งหน้ารีเฟรชทุกครั้งที่เปิด',
  'tech-ai': 'ตะกร้าเทคหลักของ NAS100 — ชิป หน่วยความจำ และสตอเรจที่ขับเคลื่อนดาต้าเซ็นเตอร์ AI บวก SpaceX เมกะแคปตัวใหม่ล่าสุดในดัชนี นี่คือกลุ่มที่ตามทุกวันทำการ',
  'ipo-watch': 'คลื่น IPO รอบหน้าอาจเปลี่ยนหน้าตา NAS100 เอง — แล็บ AI แนวหน้าสองรายจ่อเข้า NASDAQ ผู้ผลิตหน่วยความจำอันดับ 2 ของโลกเข้าเทรดเดือนนี้ และเจ้าตลาด DRAM ของจีนกำลังระดมทุนหลักพันล้านในประเทศ',
  gurus: 'นักลงทุนที่คนจับตามากที่สุดในโลก — ชื่อดังดูรวดเดียวด้านบน แล้วลงรายละเอียดพอร์ตด้านล่าง ว่าถืออะไร กำลังซื้ออะไรต่อ และกำลังทยอยขายอะไร อ้างอิง 13F ไตรมาส 1/2026 (ณ 31 มี.ค. ยื่น 15 พ.ค.) เอกสารเปิดเผยของสภาคองเกรส และรายงานการเทรดของกองทุน — ข้อมูล 13F ช้ากว่าตลาดหนึ่งไตรมาสเสมอ',
  'power-top5': 'การเข้าถึงระบบไฟฟ้า ไม่ใช่ชิป คือสิ่งที่ตัดสินมากขึ้นเรื่อย ๆ ว่า AI จะถูกสร้างที่ไหน นี่คือผู้ผลิตไฟฟ้าจดทะเบียนในสหรัฐ 5 อันดับแรกตามมูลค่าตลาด',
  nuclear: 'บิ๊กเทคเลือกนิวเคลียร์เป็นคำตอบของไฟฟ้าสำหรับ AI — Microsoft, Amazon, Google และ Meta เซ็นดีลเตาปฏิกรณ์กันครบแล้ว เราตามผู้สร้างเตารุ่นใหม่ และยูเรเนียมที่เป็นเชื้อเพลิงของมัน',
};

export function QuantWatchlistCard() {
  const t = useT();
  const [w, setW] = useState<Watchlist | null>(null);
  const [stale, setStale] = useState(false);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [sec, setSec] = useState<string | null>(null);
  const [quotes, setQuotes] = useState<Record<string, { price: number; chg: number }>>({});

  useEffect(() => {
    let alive = true;
    fetch('/api/nopoly?feed=watchlist')
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(v => { if (alive) { setW(v.data); setStale(!!v.stale); } })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  const sections = w?.sections || [];
  const checklist = useMemo(() => sections.find(s => s.checklist?.length)?.checklist || [], [sections]);

  // Only the expanded view shows prices, so only fetch quotes once it opens.
  useEffect(() => {
    if (!open) return;
    const syms = Array.from(new Set(sections.flatMap(s => (s.stocks || []).map(x => x.symbol)))).filter(s => !(s in quotes));
    if (!syms.length) return;
    let alive = true;
    fetchStockQuotes(syms).then(q => { if (alive) setQuotes(x => ({ ...x, ...q })); });
    return () => { alive = false; };
  }, [open, sections]); // eslint-disable-line react-hooks/exhaustive-deps

  if (failed) return null; // upstream down and no snapshot — hide rather than show an error card
  if (!w || !sections.length) return null;

  return (
    <div className="mm-card glass mm-qwatch">
      <div className="mm-guru-head">
        <SecHead th="Quant Watchlist" en="QUANT WATCHLIST"></SecHead>
        <button className="mm-pill sm" onClick={() => setOpen(o => !o)}>
          {open ? t('ย่อ', 'Collapse') : t('ดูเต็ม', 'Expand')}
        </button>
      </div>

      <div className="mm-mlearn-meta mono dim">
        {w.asOf && <span>{w.asOf}</span>}
        {stale && <span>· {t('ข้อมูลสำรอง (ต้นทางไม่ตอบ)', 'snapshot — upstream unreachable')}</span>}
      </div>

      {checklist.length > 0 && (
        <div className="mm-qwatch-check">
          <span className="mm-std-lbl mono">{t('เช็กลิสต์ก่อนตลาดเปิด', 'PRE-MARKET CHECKLIST')}</span>
          <ul>
            {checklist.map((c, i) => (
              <li key={i}>{c.time && <em className="mono dim">{c.time}</em>} {c.title}</li>
            ))}
          </ul>
        </div>
      )}

      {!open ? (
        <div className="mm-qwatch-tiles">
          {sections.map(s => (
            <button className="mm-qwatch-tile" key={s.id} onClick={() => { setOpen(true); setSec(s.id); }}>
              <b>{t(TH_LABEL[s.id] || s.navLabel || s.title, s.navLabel || s.title)}</b>
              {(TH_THESIS[s.id] || s.thesis) && <span className="dim">{t(TH_THESIS[s.id] || s.thesis || '', s.thesis || '')}</span>}
            </button>
          ))}
        </div>
      ) : (
        <div className="mm-qwatch-secs">
          {sections.map(s => {
            const isOpen = sec === s.id;
            return (
              <div className={`mm-qwatch-sec ${isOpen ? 'on' : ''}`} key={s.id}>
                <button className="mm-qwatch-sechead" onClick={() => setSec(isOpen ? null : s.id)}>
                  <b>{s.title}</b>
                  <span className="mono dim">{isOpen ? '−' : '+'}</span>
                </button>
                {isOpen && (
                  <div className="mm-qwatch-body">
                    {s.thesis && <p className="dim">{s.thesis}</p>}
                    <SectionBody s={s} quotes={quotes}></SectionBody>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="mm-footnote dim">
        {t('ราคาเรียลไทม์เป็นของเราเอง · เพื่อการศึกษา ไม่ใช่คำแนะนำการลงทุน',
          'Live prices are ours · education only, not investment advice')}
      </p>
    </div>
  );
}

// Each section carries a different payload. Anything we do not recognise falls
// through to title + thesis only, so an upstream addition cannot break the card.
function SectionBody({ s, quotes }: { s: WSection; quotes: Record<string, { price: number; chg: number }> }) {
  if (s.stocks?.length) {
    return (
      <ul className="mm-qwatch-list">
        {s.stocks.map(x => {
          const q = quotes[x.symbol];
          return (
            <li key={x.symbol}>
              <div className="mm-qwatch-row">
                <b className="mono">{x.symbol}</b>
                <span>{x.name}</span>
                {x.marketCap && <span className="mono dim">{x.marketCap}</span>}
                {q && <span className={`mono ${q.chg >= 0 ? 'up' : 'down'}`} style={{ marginLeft: 'auto' }}>
                  {q.price.toLocaleString(undefined, { maximumFractionDigits: 2 })} <small>{q.chg >= 0 ? '▲' : '▼'}{Math.abs(q.chg).toFixed(2)}%</small>
                </span>}
              </div>
              {x.about && <p className="dim">{x.about}</p>}
            </li>
          );
        })}
      </ul>
    );
  }
  if (s.stories?.length) {
    return (
      <ul className="mm-qwatch-list">
        {s.stories.map((x, i) => (
          <li key={i}>
            <div className="mm-qwatch-row"><b>{x.title}</b>{x.tag && <span className="mm-chip dim mono">{x.tag}</span>}</div>
            {x.status && <p className="mono dim">{x.status}</p>}
            {x.detail && <p className="dim">{x.detail}</p>}
          </li>
        ))}
      </ul>
    );
  }
  if (s.gurus?.length || s.people?.length) {
    return (
      <ul className="mm-qwatch-list">
        {(s.people || []).map((p, i) => (
          <li key={`p${i}`}>
            <div className="mm-qwatch-row"><b>{p.name}</b>{p.role && <span className="dim">{p.role}</span>}</div>
            {p.action && <p className="dim">{p.action}{p.tickers ? ` — ${p.tickers}` : ''}</p>}
          </li>
        ))}
        {(s.gurus || []).map((g, i) => (
          <li key={`g${i}`}>
            <div className="mm-qwatch-row"><b>{g.name}</b>{g.org && <span className="dim">{g.org}</span>}</div>
            {g.buying && <p className="dim"><span className="up">▲</span> {g.buying}</p>}
            {g.selling && <p className="dim"><span className="down">▼</span> {g.selling}</p>}
            {g.note && <p className="dim">{g.note}</p>}
          </li>
        ))}
      </ul>
    );
  }
  return null;
}
